/**
 * Organization membership at `users/{uid}/grants/{orgId}`. A nullish roleId
 * is an event-only membership envelope, discoverable without organization-wide
 * authority. Event roles live in its `events/{eventId}` subcollection.
 * Provenance remains optional for existing grants. The path supplies identity;
 * permissions are resolved from the organization's canonical role catalog.
 */
import { entity } from "@mhmo91/ican";
import { firestore } from "@mhmo91/ican/firestore/binding";
import type { Timestamp } from "firebase/firestore";
import { z } from "zod";
import { type Actor, Id } from "./workspace.entity.ts";

/** Provisioning intent; each event grant is nested under its organization. */
export const GrantScopeSchema = z.discriminatedUnion("kind", [
	z.object({ kind: z.literal("all-events"), orgId: Id }),
	z.object({ kind: z.literal("events"), eventIds: z.array(Id).min(1) }),
]);
export type GrantScope = z.infer<typeof GrantScopeSchema>;

export const GrantSchema = z.object({
	kind: z.literal("organization"),
	roleId: Id.nullish().describe(
		"organization-wide role; absent for event-only membership",
	),
	grantedBy: Id.nullish().describe(
		"uid of the actor who wrote this grant — an admin's own call, an invite's inviter, or a scripted-provisioning sentinel",
	),
	grantedAt: z
		.custom<Timestamp>()
		.nullish()
		.describe("server time the grant was written"),
});
export type Grant = z.infer<typeof GrantSchema>;

// read + list: self only, both keyed on the {uid} path variable. `list` is
// what session assembly needs — an actor holding event-scoped grants has
// more than one doc, so the shell lists the subcollection rather than
// getting a single doc. `{uid}` is a PARENT segment, bound during a
// subcollection list because the request targets a concrete `users/{uid}`
// parent path.
// mfa is intentionally NOT required on read/list — a member account
// mid-enrollment must still resolve its own grants to reach the enrollment
// screen before the claim flips.
//
// create/update/delete: server-only. A Firestore `.when(...)` rule can gate
// on the caller's OWN attributes and the grant doc's OWN fields, but cannot
// compare the target role being assigned against a SECOND document (the
// caller's own role) it does not itself own — `hasPermission(...)` alone
// let any `tickets.members.invite` holder write ANY roleId, including onto
// their own grant (self-promotion to the top of the catalog). `assignGrant`/
// `removeGrant` (firebase/src/members/) run the containment spine
// `createInvite.api.ts` already runs — target role exists, its permissions
// ⊆ the caller's own role's permissions, and the write's scope is tenant —
// and are the sole entry points. The same callables ALSO close the
// event-scoped-grant gap: `hasPermission`'s own-scope path-var resolution
// has no role catalog to resolve against an event id and fails closed, but
// the callable resolves the caller's authority at the ORG level and checks
// the write's scope tenancy independently, so an event-scoped re-role now
// succeeds through the callable where it never could through this rule.
export const Grant = entity<Actor>()({
	schema: GrantSchema,
	actions: ["read", "list", "create", "update", "delete"] as const,
	namespace: "tickets.grants",
})
	.pipe(({ can, pathVar, and }) => [
		can("read").when((_data, me) => me.uid.eq(pathVar("uid"))),
		can("list").when((_data, me) => me.uid.eq(pathVar("uid"))),
		can("create")
			.when(() => and())
			.requiresHandler("assignGrant"),
		can("update")
			.when(() => and())
			.requiresHandler("assignGrant"),
		can("delete")
			.when(() => and())
			.requiresHandler("removeGrant"),
	])
	.adaptor(
		firestore({ hierarchicalGrants: true, path: "users/{uid}/grants/{orgId}" }),
	);
